<template>
  <div>
    <ImageZoom
      v-model="showingZoom"
      :src="zoomedImageSrc"
      @remove="removeImage"
    />

    <ODialog
      ref="dialog"
      hideWhenLoading
      :persistent="editingDesc"
      :disableBounce="editingDesc"
    >
      <v-card v-if="row != null">
        <!-- <ODialogBar title="Evento" @close="dismiss" /> -->
        <v-card-text class="pa-6">
          <div class="subtitle-2">Data/hora</div>
          <ODateTimeInput2 v-model="timestamp" :clearable="false" />

          <div class="subtitle-2 mt-6">Descrição</div>

          <v-sheet
            class="descSheet"
            style="height: 140px"
            v-if="!editingDesc"
            @click="startEditDesc"
          >
            {{ row.description }}
          </v-sheet>

          <v-textarea
            v-else
            ref="theTextarea"
            style="height: 140px"
            class="mt-0 pt-0"
            v-model="description"
            @blur="endEditDesc"
            @keydown="checkControlV"
          />

          <div class="subtitle-2 mt-6">Cor</div>
          <div>
            <v-btn-toggle v-model="selectedColorIndex">
              <v-btn small v-for="c in colors" :key="c" :color="c">
                <v-icon
                  v-if="currentColor == c"
                  color="white"
                  v-html="'mdi-check'"
                />
              </v-btn>
            </v-btn-toggle>
          </div>

          <div class="subtitle-2 mt-6">Imagens</div>
          <div class="thumbs">
            <v-img
              v-for="image in images"
              class="elevation-1"
              style="cursor: pointer"
              :key="image.id"
              :src="image.srcUrl"
              :height="100"
              :width="100"
              :aspect-ratio="1"
              @click="zoomImage(image)"
            />
            <div class="uploading-loader" v-if="isUploading">
              <v-progress-circular indeterminate />
            </div>
            <button @click="addImageFromFilePicker"
              >
              <v-icon>mdi-plus</v-icon>
            </button>
          </div>

          <div class="h-row">
            <v-spacer />
            <v-btn color="red" text @click="confirmDelete">
              Remover
            </v-btn>
          </div>
        </v-card-text>
        <!-- <ODialogButtons class="pa-6" @cancel="dismiss" @apply="apply" /> -->
      </v-card>
    </ODialog>
  </div>
</template>

<script>
import moment from 'moment'
import { openBlob } from 'oderon-vue/utils'
import { Task } from '@/models'
import ImageZoom from './ImageZoom.vue'

export default {
  components: {
    ODateTimeInput2: require('oderon-vue/cpts/ODateTimeInput2.vue').default,
    ImageZoom
  },
  data() {
    return {
      ...this.reset(),

      images: [],

      colors: ['red', 'orange', 'green', 'blue', 'deep-purple', 'grey darken-1', 'pink', 'purple', 'teal darken-1', 'lime darken-1', 'brown']
    }
  },
  computed: {
    currentColor() {
      if (this.selectedColorIndex == null) return null
      return this.colors[this.selectedColorIndex]
    }
  },
  watch: {
    timestamp(newVal, oldVal) {
      if (newVal != null && newVal != oldVal) {
        this.patch({ timestamp: newVal })
      }
    },
    selectedColorIndex(newVal, oldVal) {
      // console.log('color changed', newVal, oldVal)
      // se um está null, o change foi disparado pelo open(),
      // e não por interação do usuário, então não faz nada.
      if (newVal == null || oldVal == null) return null
      if (newVal != oldVal) this.patch({ color: this.colors[newVal] })
    }
  },
  methods: {
    reset() {
      return {
        row: null,
        selectedColorIndex: null,
        description: null,
        timestamp: null,
        showingZoom: false,
        zoomedImage: null,
        zoomedImageSrc: null,
        editingDesc: false,
        isUploading: false
      }
    },

    open(taskId) {
      this.taskId = taskId
      Object.assign(this, this.reset())
      this.$refs.dialog.$loading(() => this.fetchData())
      return this.$refs.dialog.$open()
    },

    dismiss() {
      this.$refs.dialog.$dismiss()
    },

    async fetchData() {
      this.row = await Task.findById(this.taskId)
      this.selectedColorIndex = this.colors.indexOf(this.row.color)
      this.description = this.row.description
      this.timestamp = moment(this.row.timestamp).toDate()
      this.images = await Task.findImages(this.taskId)

      // Não espera
      Promise.all(
        this.images.map(async image => {
          let srcUrl = await this.getSrcUrl(image)
          this.$set(image, 'srcUrl', srcUrl)
        })
      )
    },

    async patch(what) {
      await Task.patch(this.taskId, what)
    },

    async checkControlV(evt) {
      // console.log('checking ctrl v', evt)
      let key = evt && evt.key,
        ctrl = evt && evt.ctrlKey

      if ((key == 'v' || key == 'V') && ctrl) {
        let gotImage = await this.tryGetImageFromClipboard()
        if (gotImage) evt.stopPropagation()
      }
    },

    async tryGetImageFromClipboard() {
      const clipboardItems = await navigator.clipboard.read()

      for (const clipboardItem of clipboardItems) {
        for (const type of clipboardItem.types) {
          if (type.startsWith('image')) {
            // console.log('using type', type)
            let blob = await clipboardItem.getType(type)
            await this.addImage(blob)
            return true
          }
        }
      }
    },

    startEditDesc() {
      this.editingDesc = true
      setTimeout(() => this.$refs.theTextarea.focus(), 100)
    },

    lostFocus() {
      setTimeout(() => this.endEditDesc(), 100)
    },

    endEditDesc() {
      this.editingDesc = false
      if (this.row.description != this.description) {
        this.row.description = this.description
        this.patch({ description: this.description })
      }
    },

    async addImageFromFilePicker() {
      let blob = await openBlob({ accept: 'image/*', asBlob: true })
      if (blob != null) {
        await this.addImage(blob)
      }
    },

    async addImage(blob) {
      this.isUploading = true
      let created = await Task.addImage(this.taskId, blob)
      this.images.push(created)
      let srcUrl = await this.getSrcUrl(created)
      this.$set(created, 'srcUrl', srcUrl)
      this.isUploading = false
    },

    zoomImage(image) {
      this.zoomedImage = image
      this.zoomedImageSrc = image.srcUrl
      this.showingZoom = true
    },

    async removeImage() {
      let imageId = this.zoomedImage.id
      await Task.removeImage(this.taskId, imageId)
      let idx = this.images.findIndex(x => x.id == imageId)
      this.images.splice(idx, 1)
    },

    async getSrcUrl(image) {
      let file = await Task.getImageBlob(this.taskId, image.id)
      return URL.createObjectURL(file.blob)
    },

    async confirmDelete() {
      let ok = await this.$confirmDelete(
        'Tem certeza que deseja desativar esta tarefa? É possível restaurá-la depois.'
      )
      if (ok) {
        await Task.delete(this.taskId)
        this.dismiss()
      }
    }
  }
}
</script>

<style lang="sass">
.thumbs
  display: grid
  overflow-x: auto
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr))
  gap: 1rem
  padding: 4px

  button
    width: 100px
    height: 100px
    background-color: #fafafa

    &:hover
      background-color: #eee

  .uploading-loader
    width: 100px
    height: 100px
    display: flex
    flex-flow: column
    align-items: center
    justify-content: center

.descSheet
  cursor: pointer
  background-color: #f7f7f7 !important
  padding: 4px
  &:hover
    background-color: #f3f3f3
</style>
